import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";


import { AgGridReact } from "ag-grid-react";
import { getEmployeeList } from "src/apis/employeeApi";
import { getItemPopupList } from "src/apis/itemApi";
import { BRDiv, SmallTitleSpan } from "src/components/common/common";
import { CheckboxButtonForm } from "src/components/common/form/checkbox/CheckboxButtonForm";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { formatDate } from "src/utils/string/stringUtil";
import { defaultPRD3010_R01HeaderDto, PRD3010_R01DetailDto, PRD3010_R01HeaderDto, PRD3010_R01LotDto } from "./Dto_PRD3010_R01";
import { usePRD3010_R01Store } from "./Store_PRD3010_R01";



interface Props {
	editable: boolean

	defaultPRD3010_R01HeaderRow: PRD3010_R01HeaderDto;

	handleFetchBomProductList: ( itemCd: string, warehouseCd: string, logicCenter: string ) => void;
	handleFetchLotList: ( productWorkNo: string ) => void;
}

export const Info_PRD3010_R01 = (props: Props) => {

	const { 
		editable, 
		defaultPRD3010_R01HeaderRow,
		handleFetchBomProductList, handleFetchLotList,
	} = props;

	// 
	const { wmsCdList, compCdList, employeeCdList, itemStatusList, workPlaceList, warehouseCdList } = useCommonCodeStore();
	const { setCodeSearchModalProps, setAddressModalProps } = useUiuxStore();
	const { selectedPRD3010_R01,
			editPRD3010_R01, setEditPRD3010_R01,
			isInitialLoad, setIsInitialLoad,
			warehouseCdListToUseList,
	} = usePRD3010_R01Store();

	// 매출일자 달력 팝업 상태
	const [productDtPickerVisible, setProductDtPickerVisible] = useState(false);
	const [productDtPickerPos, setProductDtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});


	const custInfoHandleFoucsId = 'infoCustInput';
	const empInfoHandleFoucsId = 'infoEmpInput';
	const addressHandleFoucsId = 'addressInput2';

	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'itemCd',
            category: "품목",
            focusId: "itemCd_search",
			responseMapping: {
				itemCd : 'itemCd',
				itemNm : 'itemNm',
				itemSpec : 'itemSpec',
				itemUnit : 'itemUnit'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getItemPopupList(code, '', '', '');
				return employeeResponse.data.data.find(value => value.itemCd === code);
			}
        },
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: empInfoHandleFoucsId,
			responseMapping: {
				empNo: 'orderEmp',
				empNm: 'orderEmpNm'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			}
		}
	];

	// Info 폼 이벤트 훅 사용
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editPRD3010_R01,
		setEditData: setEditPRD3010_R01,
		defaultData: defaultPRD3010_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'PRD3010_R01'
	});

	// info 초기값 설정
	useEffect(() => {
		if(editPRD3010_R01) {
			setIsInitialLoad(true); // 새로운 행 선택 시 초기 로드 플래그를 true로 설정
			handleFetchLotList(editPRD3010_R01.productOrderNo!);
		}
	}, [selectedPRD3010_R01])

	useEffect(() => {
		// 초기 로드 시에는 Content에서 이미 호출했으므로 건너뜁니다.
		if (isInitialLoad) {
			setIsInitialLoad(false);
			return;
		}

		if(editPRD3010_R01?.itemCd && editPRD3010_R01?.outWarehouseCd && editPRD3010_R01?.logicCenter) {
			handleFetchBomProductList(editPRD3010_R01.itemCd, editPRD3010_R01.outWarehouseCd, editPRD3010_R01.logicCenter);
		}
	}, [editPRD3010_R01?.outWarehouseCd, editPRD3010_R01?.logicCenter])

	const handleDateChange = useCallback((field: keyof PRD3010_R01HeaderDto, value: any) => {
		setEditPRD3010_R01({
			...defaultPRD3010_R01HeaderRow,
			...editPRD3010_R01,
			[field]: value
		});
	}, [editPRD3010_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		let closeTag = editPRD3010_R01?.closeTag || 'N';
		if(Number(editPRD3010_R01?.orderQty) === Number(editPRD3010_R01?.productQty)) {
			closeTag = 'Y';
		}else {
			closeTag = 'N';
		}

		let additionalUpdates = {};
		// 입고물류(logicCenter)가 변경될 때, 해당 물류의 첫 번째 창고로 warehouseCd 자동 세팅
		if (name === 'logicCenter') {
			const availableWarehouses = warehouseCdListToUseList(value);
			additionalUpdates = {
				warehouseCd: availableWarehouses[0]?.warehouseCd || ''
			};
		}

		setEditPRD3010_R01({
			...defaultPRD3010_R01HeaderRow,
			...editPRD3010_R01,
			[name]: value,
			closeTag: closeTag,
			...additionalUpdates
		});
	}, [editPRD3010_R01, defaultPRD3010_R01HeaderRow, warehouseCdListToUseList]);



	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] overflow-y-auto border border-gray-300">
					<InputForm title="지시No" value={editPRD3010_R01?.productResultNo || ""} 
						editable={false}
						name="productResultNo" 
						onChange={handleChange} />
					<DropDownForm
						title="사업장"
						name="compCd"
						initLabelLess={true}
						value={editPRD3010_R01?.compCd}
						data={compCdList}
						editable={true}
						onChange={handleChange}
					/>
					<BRDiv />
					<DropDownForm
						title="작업정보"
						name="workplaceCd"
						initLabelLess={true}
						value={editPRD3010_R01?.workplaceCd}
						data={workPlaceList}
						editable={false}
						onChange={handleChange}
					/>
					<SingleDateForm
						title="생산일자"
						value={editPRD3010_R01?.productDt ? new Date(editPRD3010_R01.productDt) : undefined}
						setValue={(value: Date | undefined) => handleDateChange('productDt', formatDate(value))}
						editable={true}
						visible={productDtPickerVisible}				setVisible={setProductDtPickerVisible}
						onChange={handleChange}
						pickerPos={productDtPickerPos}					setPickerPos={setProductDtPickerPos}
					/>
					<BRDiv />
					<DropDownForm
						title="입고물류"
						name="logicCenter"
						initLabelLess={true}
						value={editPRD3010_R01?.logicCenter}
						data={wmsCdList}
						editable={false}
						onChange={handleChange}
					/>
					<DropDownForm<WareHouseDto> 
						title="입고창고"
						name="warehouseCd" 
						data={warehouseCdListToUseList(editPRD3010_R01?.logicCenter || '')}
						editable={true}
						value={editPRD3010_R01?.warehouseCd}
						initLabelLess={true}
						onChange={handleChange}
					/>
					<BRDiv />
					<CodeForm
						label="생산담당자"
						value={editPRD3010_R01?.orderEmp || ""}
						custNmValue={editPRD3010_R01?.orderEmpNm || ""}
						editable={true}
						name="orderEmp"
						handleChange={getFieldHandlers('empNo').onChange}
						onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
						onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
					/>
					<BRDiv />
					<CodeForm
						label="품목코드"
						value={editPRD3010_R01?.itemCd || ""}
						custNmValue={editPRD3010_R01?.itemNm || ""}
						editable={false}
						name="itemCd"
						handleChange={getFieldHandlers('itemCd').onChange}
						onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
						onKeyDown={getFieldHandlers('itemCd').onEnterKeyDown}
					/>
					<p 
						className={`bg-gray-disable text-left w-[150px] min-w-[150px] h-[20px] mr-[5px] pl-[2px] rounded-[4px] border-[1px] truncate cursor-pointer`}
						id={`itemSpec_codeLabel`}
						tabIndex={0}
					>
						{editPRD3010_R01?.itemSpec || ""}
					</p>
					<p 
						className={`bg-gray-disable text-left w-[150px] min-w-[150px] h-[20px] mr-[5px] pl-[2px] rounded-[4px] border-[1px] truncate cursor-pointer`}
						id={`itemUnit_codeLabel`}
						tabIndex={0}
					>
						{editPRD3010_R01?.itemUnit || ""}
					</p>
					<BRDiv />
					<InputForm title="작업지시잔량" value={editPRD3010_R01?.orderQty || 0} 
						editable={false}
						name="orderQty" 
						onChange={handleChange} />
					<DropDownForm
						title="생산품목등급"
						name="itemStatus"
						data={itemStatusList}
						editable={true}
						value={editPRD3010_R01?.itemStatus}
						initLabelLess={true}
						onChange={handleChange}
					/>
					<BRDiv />
					<InputForm title="생산양품량" value={editPRD3010_R01?.productQty || 0} 
						editable={false}
						name="productQty" 
						onChange={handleChange} />
					<InputForm title="생산불량수량" value={editPRD3010_R01?.badQty || 0} 
						editable={false}
						name="badQty" 
						onChange={handleChange} />
					<BRDiv />

					
					<CheckboxButtonForm
						title="마감여부"
						value={editPRD3010_R01?.closeTag || 'N'}
						name="closeTag"
						editable={true}
						onChange={(e) => { setEditPRD3010_R01({
							...defaultPRD3010_R01HeaderRow,
							...editPRD3010_R01,
							closeTag: e.target.value === 'Y' ? 'Y' : 'N' }) 
						}}
					/>
					<BRDiv />
					
					<InputForm
						title="비고"
						value={editPRD3010_R01?.remark || ""}
						editable={true}
						name="remark"
						wrapperOption={{
							wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[10px]",
							titleStyle: "w-[100px] min-w-[100px] text-left",
							inputStyle: "w-full min-w-full"
						}}
						onChange={handleChange}
					/>
		</div>
	)
}